import React, { useMemo } from "react";

import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";

import useIdToken from "hooks/useIdToken.hook";

import {
  AssistantFeatureWalkthroughActions,
  AssistantLauncher,
  AssistantPanel,
  AssistantPromptSection,
} from "./components";
import { ContentSection } from "./components/content-section";
import { useAssistantPanelPlacement } from "./components/panel/hooks/useAssistantPanelPlacement";
import { useAssistantPanelResize } from "./components/panel/hooks/useAssistantPanelResize";
import {
  ASSISTANT_PANEL_POSITION_STORAGE_KEY,
  ASSISTANT_LAUNCHER_POSITION_STORAGE_KEY,
} from "./constants";
import { AssistantInteractionContext } from "./context";
import { useAssistantConversation } from "./hooks/useAssistantConversation";
import { useAssistantDrag } from "./hooks/useAssistantDrag";
import { useAssistantFocus } from "./hooks/useAssistantFocus";
import { useAssistantInteractions } from "./hooks/useAssistantInteractions";
import { useAssistantWalkthroughFeatures } from "./hooks/useAssistantWalkthroughFeatures";
import { AssistantHighlight } from "./styles";
import { IAssistantProps } from "./types";
import {
  getAssistantPage,
  getAssistantScenarioCounts,
  getAssistantScreen,
} from "./utils";

const Assistant: React.FC<IAssistantProps> = ({ children }) => {
  const location = useLocation();
  const token = useIdToken();
  const page = getAssistantPage(location.pathname, token);
  const identity = useMemo(
    () =>
      JSON.stringify({
        user: token?.userId,
        category: token?.category,
        permissions: token?.permissions,
      }),
    [token?.userId, token?.category, token?.permissions]
  );
  const conversation = useAssistantConversation(page, identity);
  const walkthroughFeatures = useAssistantWalkthroughFeatures({
    identity,
    enabled: Boolean(token?.userId),
    open: conversation.isOpen,
  });
  const {
    featureWalkthrough,
    walkthrough,
    handleOpen,
    handleClose,
    handleAskQuestion,
    handleSelectScenario,
    handleFinishFeature,
    handleDoneFeature,
    handleSkipScenario,
  } = useAssistantInteractions({
    identity,
    page,
    onOpenConversation: conversation.handleOpen,
    onCloseConversation: conversation.handleClose,
    onAsk: conversation.handleAsk,
    onAcknowledge: walkthroughFeatures.handleAcknowledge,
    onScenarioCompleted: walkthroughFeatures.handleRefresh,
    onCompleteGuide: walkthroughFeatures.handleCompleteGuide,
  });
  const { feature } = featureWalkthrough;
  const scenarioCounts = getAssistantScenarioCounts(feature?.scenarios ?? []);
  const visible =
    conversation.isOpen || Boolean(walkthrough.guide) || Boolean(feature);
  const screen = getAssistantScreen(
    walkthrough.guide,
    walkthrough.index,
    feature,
    featureWalkthrough.hasFinished
  );
  const focus = useAssistantFocus(visible, Boolean(walkthrough.guide), screen);

  const panelDrag = useAssistantDrag({
    elementRef: focus.panelRef,
    visible,
    storageKey: ASSISTANT_PANEL_POSITION_STORAGE_KEY,
    resetOnHide: true,
  });
  const launcherDrag = useAssistantDrag({
    elementRef: focus.launcherRef,
    storageKey: ASSISTANT_LAUNCHER_POSITION_STORAGE_KEY,
    visible: !visible,
    resetOnHide: true,
  });

  const panelResize = useAssistantPanelResize({
    panelRef: focus.panelRef,
    visible,
    onPositionChange: panelDrag.handlePositionChange,
  });

  const panelPosition = useAssistantPanelPlacement({
    panelRef: focus.panelRef,
    visible,
    preferredPosition: panelResize.position ?? panelDrag.position,
    highlights: walkthrough.highlights,
    dragging: panelDrag.isDragging || panelResize.isResizeActive,
  });

  return (
    <AssistantInteractionContext.Provider value={visible}>
      {children}
      {token?.userId &&
        createPortal(
          <>
            {walkthrough.highlights.map(({ contrastRing, ...highlight }) => (
              <AssistantHighlight
                key={`${highlight.top}:${highlight.left}:${highlight.width}:${highlight.height}`}
                aria-hidden
                contrastRing={contrastRing}
                sx={highlight}
              />
            ))}
            {visible ? (
              <AssistantPanel
                ref={focus.panelRef}
                onClose={handleClose}
                panelPosition={panelPosition}
                dragHandlers={panelDrag.handlers}
                dragging={panelDrag.isDragging}
                panelSize={panelResize.size}
                resizeHandlers={panelResize.handlers}
              >
                <ContentSection
                  walkthroughGuide={walkthrough.guide}
                  walkthroughStep={walkthrough.step}
                  walkthroughIndex={walkthrough.index}
                  walkthroughHighlights={walkthrough.highlights}
                  walkthroughSaving={walkthrough.isSaving}
                  walkthroughSaveFailed={walkthrough.hasSaveFailed}
                  walkthroughHandleBack={walkthrough.handleBack}
                  walkthroughHandleAdvance={walkthrough.handleAdvance}
                  walkthroughHandleSkip={walkthrough.handleSkip}
                  walkthroughHandleRestart={walkthrough.handleRestart}
                  walkthroughHandleCancel={walkthrough.handleCancel}
                  walkthroughCompleted={walkthrough.isCompleted}
                  walkthroughHandleDismissCompletion={
                    walkthrough.handleDismissCompletion
                  }
                  feature={feature}
                  featurePending={featureWalkthrough.isPending}
                  featureFinished={featureWalkthrough.hasFinished}
                  onResumeFeature={featureWalkthrough.handleResume}
                  handleSkipScenario={handleSkipScenario}
                  handleSelectScenario={handleSelectScenario}
                  walkthroughFeatures={walkthroughFeatures.walkthroughFeatures}
                  pendingWalkthroughFeatureIds={walkthroughFeatures.pendingIds}
                  onDismissWalkthroughFeature={
                    walkthroughFeatures.handleAcknowledge
                  }
                  submittedQuestion={conversation.submittedQuestion}
                  answer={conversation.answer}
                  questionPending={conversation.isPending}
                  handleAskQuestion={handleAskQuestion}
                />
                {feature && (
                  <AssistantFeatureWalkthroughActions
                    finished={featureWalkthrough.hasFinished}
                    onDone={handleDoneFeature}
                    completed={scenarioCounts.completed}
                    skipped={scenarioCounts.skipped}
                    pending={featureWalkthrough.isPending}
                    onFinish={handleFinishFeature}
                  />
                )}
                <AssistantPromptSection
                  question={conversation.question}
                  pending={conversation.isPending}
                  onQuestionChange={conversation.handleQuestionChange}
                  onAsk={handleAskQuestion}
                />
              </AssistantPanel>
            ) : (
              <AssistantLauncher
                hasNewWalkthroughFeatures={
                  walkthroughFeatures.walkthroughFeatures.length > 0
                }
                ref={focus.launcherRef}
                onOpen={handleOpen}
                launcherPosition={launcherDrag.position}
                dragHandlers={launcherDrag.handlers}
                dragging={launcherDrag.isDragging}
              />
            )}
          </>,
          document.body
        )}
    </AssistantInteractionContext.Provider>
  );
};

export default Assistant;
